<template>
  <BasePageTemplate :title="resetCredentialsTitleString" :description="resetCredentialsDescriptionString" v-model:error="error">
    <DynamicForm v-model:data="data" :definition="formDefinition" :form-id="formId">
      <!-- Username field -->
      <Field :label="usernameString" is-required target="username">
        <IconField>
          <InputIcon>
            <OutlinedIcon icon="person" class="text-xl" />
          </InputIcon>

          <InputText
            :ref="usernameInputId"
            v-model="data.username"
            fluid
            :disabled="disabled"
            :pt="{ root: { autocomplete: 'off' } }"
            @enter="onUsernameClickAsync"
          />
        </IconField>
      </Field>

      <!-- Email field -->
      <Field :label="emailString" is-required target="email">
        <IconField>
          <InputIcon>
            <OutlinedIcon icon="mail" class="text-xl" />
          </InputIcon>

          <InputText
            :ref="emailInputId"
            v-model="data.email"
            fluid
            :disabled="disabled"
            :pt="{ root: { autocomplete: 'off' } }"
            @enter="onResetCredentialsClick"
          />
        </IconField>
      </Field>

      <MainButton :label="nextString" :loading="disabled" :disabled="disabled" @click="onResetCredentialsClick" />
    </DynamicForm>

    <!-- Other buttons -->
    <div class="flex align-center justify-center">
      <Button
        icon="chevron_left"
        rounded
        outlined
        severity="secondary"
        :disabled="disabled"
        :aria-label="backString"
        v-tooltip.top="{ value: backString, pt: { text: 'dark' } }"
        @click="onBackClickAsync"
      ></Button>
    </div>
  </BasePageTemplate>
</template>

<script setup lang="ts">
import { Endpoints } from "@authentication/constants";
import { useRedirectionTools } from "@authentication/hooks";
import { AuthenticationApiService } from "@authentication/services";
import { OtpResetCredentialsResponseType, PreResetCredentialsType } from "@authentication/types";
import { BasePageTemplate, PasswordInput } from "@authentication/ui";
import { IFormDefinition } from "@common/core";
import { useForm } from "@common/hooks";
import { ProblemDetailsType } from "@common/types";
import { Button, DynamicForm, Field, InputText, MainButton, OutlinedIcon } from "@common/ui";
import { useTranslation } from "i18next-vue";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import { nextTick, onMounted, ref, useId, useTemplateRef } from "vue";
import * as yup from "yup";

// Models
const data = defineModel<PreResetCredentialsType>("data", { required: true });

// Emits
const emit = defineEmits<{
  (e: "next", data: OtpResetCredentialsResponseType): void;
}>();

// Constants
const formId = useId();
const emailInputId = useId();
const usernameInputId = useId();

// Variables
const { t } = useTranslation();
const redirectionTools = useRedirectionTools();
const dataFrom = useForm([formId]);
const emailInput = useTemplateRef<InstanceType<typeof PasswordInput>>(emailInputId);
const usernameInput = useTemplateRef<InstanceType<typeof PasswordInput>>(usernameInputId);
const disabled = ref(false);
const error = ref<ProblemDetailsType | null>();

// Strings
const backString = t("commons.back");
const emailString = t("commons.email");
const nextString = t("commons.next");
const preResetValidationErrorString = t("reset_credentials.pre_reset_invalid_error");
const resetCredentialsDescriptionString = t("reset_credentials.form_description");
const resetCredentialsTitleString = t("reset_credentials.form_title");
const usernameString = t("identity_server_application.username");

// Forms
const formDefinition = {
  username: {
    spec: yup.string().required().min(3).label(usernameString),
  },
  email: {
    spec: yup.string().required().email().label(emailString),
  },
} as IFormDefinition;

// Methods
const onBackClickAsync = async () => {
  await redirectionTools.redirectToAsync(Endpoints.pageSignInPath);
};

const onResetCredentialsClick = async () => {
  disabled.value = true;
  error.value = null;

  dataFrom.cleanErrors();

  if (!(await dataFrom.isValidAsync())) {
    disabled.value = false;
    return;
  }

  AuthenticationApiService.initResetCredentialsAsync(data.value)
    .then((response) => {
      if (response) {
        emit("next", response);
      } else {
        setErrorMessage(preResetValidationErrorString);
      }
    })
    .catch((e) => {
      error.value = e;
    })
    .finally(() => {
      disabled.value = false;
    });
};

const onUsernameClickAsync = async () => {
  await nextTick(() => {
    emailInput.value?.$el.focus();
  });
};

const setErrorMessage = (message: string) => {
  error.value = {
    status: 404,
    detail: message,
    title: "",
    instance: "",
    type: "",
  };
};

// Hooks
onMounted(async () => {
  await nextTick(() => {
    usernameInput.value?.$el.focus();
  });
});
</script>
